// M4 achievements (Shale & Deepwater). check(state) is pure and cheap (day gate first); false on an empty/bare state.
// State the sim must maintain for these (docs/M4_SPEC.md §16):
//   well.lateral { lengthFt, inZoneFrac, stages, padUid }, well.waterDepthFt, well.offshore, well.fieldId, well.prod,
//   well.sagd (SAGD pair), company.stats.{negativeDayHeld, hedgeGainsM4, zeroFlareYears, ducsCompleted, exportedBbl}
import { ownWells } from '../achWells.js';
import { dateToDay } from '../../core/time.js';

const producing = (w) => !!w.prod || w.status === 'PRODUCING';
const stat = (s, k) => s?.company?.stats?.[k] ?? 0;
const day = (s) => s?.day ?? -Infinity;
const lat = (w) => w?.lateral?.lengthFt ?? 0;
const solvent = (s) => (s?.company?.cash ?? -1) > 0;

const D2000 = dateToDay(2000, 1, 1), D_MORATORIUM_END = dateToDay(2010, 10, 12), D_NEG = dateToDay(2020, 4, 21), D_NEG_END = dateToDay(2021, 1, 1);
const D2017 = dateToDay(2017, 1, 1), D2018 = dateToDay(2018, 1, 1), D2015 = dateToDay(2015, 1, 1), D2016 = dateToDay(2016, 1, 1);
const D_EXPORT = dateToDay(2015, 12, 18), D2019 = dateToDay(2019, 1, 1);

const RAW = [
  { id: 'frac_spread', name: 'Frac Spread', icon: 'drop', era: 'shale',
    desc: 'Complete a horizontal well with twenty or more frac stages.',
    check: (s) => ownWells(s).some((w) => (w.lateral?.stages ?? 0) >= 20 && producing(w)) },
  { id: 'two_mile_lateral', name: 'Two-Mile Lateral', icon: 'ruler', era: 'shale',
    desc: 'Drill a lateral of 10,000 feet or more.',
    check: (s) => ownWells(s).some((w) => lat(w) >= 10000) },
  { id: 'three_miles_sideways', name: 'Three Miles Sideways', icon: 'ruler', era: 'shale',
    desc: 'Drill a 15,000-foot lateral.',
    check: (s) => ownWells(s).some((w) => lat(w) >= 15000) },
  { id: 'in_the_zone', name: 'In the Zone', icon: 'target', era: 'shale',
    desc: 'Keep 95% of a 7,500-foot lateral inside the target window.',
    check: (s) => ownWells(s).some((w) => lat(w) >= 7500 && (w.lateral?.inZoneFrac ?? 0) >= 0.95) },
  { id: 'eight_on_a_pad', name: 'Eight on a Pad', icon: 'grid', era: 'shale',
    desc: 'Drill eight wells from a single pad.',
    check: (s) => { const n = {}; for (const w of ownWells(s)) { const p = w.lateral?.padUid; if (p != null && (n[p] = (n[p] ?? 0) + 1) >= 8) return true; } return false; } },
  { id: 'duc_season', name: 'DUC Season', icon: 'duck', era: 'shale',
    desc: 'Complete ten drilled-but-uncompleted wells.',
    check: (s) => stat(s, 'ducsCompleted') >= 10 },
  { id: 'hedged_at_ninety', name: 'Hedged at Ninety', icon: 'shield', era: 'shale',
    desc: 'Collect a million dollars from your hedge book during the 2015 crash.',
    check: (s) => day(s) >= D2015 && day(s) < D2016 && stat(s, 'hedgeGainsM4') >= 1e6 },
  { id: 'price_war_survivor', name: 'Lower for Longer', icon: 'barrel', era: 'shale',
    desc: 'Still solvent in 2017 with a shale well producing.',
    check: (s) => day(s) >= D2017 && day(s) < D2018 && solvent(s) && ownWells(s).some((w) => producing(w) && lat(w) > 0) },
  { id: 'minus_thirty_seven', name: 'Minus Thirty-Seven', icon: 'barrel', era: 'shale',
    desc: 'Come through 20 April 2020 without selling a barrel below zero.',
    check: (s) => day(s) >= D_NEG && day(s) < D_NEG_END && stat(s, 'negativeDayHeld') === 1 && solvent(s) },
  { id: 'two_barriers', name: 'Two Barriers', icon: 'shield', era: 'shale',
    desc: 'Complete a deepwater well in 5,000 feet of water under the post-Macondo rules.',
    check: (s) => day(s) >= D_MORATORIUM_END && ownWells(s).some((w) => (w.waterDepthFt ?? 0) >= 5000 && producing(w) && (w.spudDay ?? 0) >= D_MORATORIUM_END) },
  { id: 'mile_and_a_half_down', name: 'A Mile and a Half Down', icon: 'wave', era: 'shale',
    desc: 'Spud a well in 7,500 feet of water.',
    check: (s) => ownWells(s).some((w) => (w.waterDepthFt ?? 0) >= 7500) },
  { id: 'under_the_salt', name: 'Under the Salt', icon: 'layers', era: 'shale',
    desc: 'Produce oil from the Brazilian pre-salt.',
    check: (s) => ownWells(s).some((w) => w.fieldId === 'brazil_presalt' && producing(w)) },
  { id: 'liza', name: 'Liza', icon: 'flag', era: 'shale',
    desc: 'Produce oil offshore Guyana.',
    check: (s) => ownWells(s).some((w) => w.fieldId === 'guyana_stabroek' && producing(w)) },
  { id: 'steam_and_patience', name: 'Steam and Patience', icon: 'flame', era: 'shale',
    desc: 'Bring a SAGD well pair to production in the oil sands.',
    check: (s) => ownWells(s).some((w) => w.fieldId === 'athabasca_sagd' && producing(w)) },
  { id: 'first_cargo', name: 'First Cargo', icon: 'ship', era: 'shale',
    desc: 'Export a million barrels of American crude after the ban is lifted.',
    check: (s) => day(s) >= D_EXPORT && stat(s, 'exportedBbl') >= 1e6 },
  { id: 'no_flare', name: 'Lights Out', icon: 'leaf', era: 'shale',
    desc: 'Run a full year with gas-producing wells and zero routine flaring.',
    check: (s) => day(s) >= D2019 && stat(s, 'zeroFlareYears') >= 1 },
];

// Every M4 achievement is locked before 1 Jan 2000, whatever the state says (M1-M3 invariance, tested).
export const M4_ACHIEVEMENTS = RAW.map((a) => ({ ...a, check: (s) => day(s) >= D2000 && !!a.check(s) }));
